import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslate } from "helpers";
import { Errors } from "services/forms/validation/types";
import { FleetTireSetEditFormValues, FleetTireSetUsage } from "services/api/fleet/tires/types";
import { initialFleetTireSetEditForm } from "services/api/fleet/tires/consts";
import { getFleetTireSetAPI, demountFleetTireSetAPI } from "services/api/fleet/tires/api";
import { transformFleetTireSetEditForm } from "services/api/fleet/tires/transformations";
import { formatKm, getDimensionSet, getTireType, getTireManufacturers } from "services/api/fleet/tires/helpers";
import { getFleetWarehouseAPI } from "services/api/fleet/warehouses/api";

import { Modal, SuccessOverlay } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import successAnimation from "ui/theme/animations/success.json";

import { TireSetDemountEditCardProps, DemountFormValues } from "./types";
import DemountStep1 from "./components/DemountStep1";
import DemountStep2 from "./components/DemountStep2";
import DemountSummary from "./components/DemountSummary";
import TireSetDemountIntro from "./components/TireSetDemountIntro/TireSetDemountIntro";

interface ResolvedDemountNames {
  targetWarehouse?: { name?: string | null; address?: string | null };
  newTireSet?: { code?: string | null; dimension?: string | null; type?: string | null; manufacturer?: string | null };
}

const initialDemountFormValues: DemountFormValues = {
  targetWarehouseId: null,
  transferDate: new Date(),
  kmTraveled: null,
  newTireSetId: null,
};

const TireSetDemountEditCard: React.FC<TireSetDemountEditCardProps> = (props) => {
  const { show, tireSetId, onSuccess, onClose } = props;
  const { t } = useTranslate();

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [currentSet, setCurrentSet] = useState<FleetTireSetEditFormValues>(initialFleetTireSetEditForm);
  const [formValues, setFormValues] = useState<DemountFormValues>(initialDemountFormValues);
  const [errors, setErrors] = useState<Errors>({});
  const [resolvedNames, setResolvedNames] = useState<ResolvedDemountNames>({});
  const [openUsage, setOpenUsage] = useState<FleetTireSetUsage | null>(null);

  useEffect(() => {
    if (show && tireSetId) {
      setStepCurrent(0);
      setFormValues({ ...initialDemountFormValues, transferDate: new Date() });
      setErrors({});
      setResolvedNames({});
      fetchTireSet(tireSetId);
    }
  }, [show, tireSetId]);

  const fetchTireSet = async (id: string) => {
    setIsLoading(true);
    try {
      const result = await getFleetTireSetAPI(id);
      if (result) {
        const setForm = transformFleetTireSetEditForm(result);
        setCurrentSet(setForm);
        setOpenUsage(result.usages.find((usage) => usage.isOpen) ?? null);
      }
    } catch { /* */ }
    setIsLoading(false);
  };

  const handleChange = useCallback((partial: Partial<DemountFormValues>) => {
    setFormValues((prev) => ({ ...prev, ...partial }));
    setErrors({});
  }, []);

  const validateStep = (): boolean => {
    const newErrors: Errors = {};

    if (stepCurrent === 0) {
      if (!formValues.targetWarehouseId) {
        newErrors.targetWarehouseId = "Odaberite skladište";
      }
      if (!formValues.transferDate) {
        newErrors.transferDate = "Odaberite datum demontaže";
      }
      if (formValues.kmTraveled == null) {
        newErrors.kmTraveled = "Unesite trenutnu kilometražu vozila";
      } else if (openUsage && formValues.kmTraveled < openUsage.mountKm) {
        newErrors.kmTraveled = `Kilometraža kod skidanja ne može biti manja od kilometraže kod montaže (${formatKm(openUsage.mountKm)})`;
      }
    }

    if (stepCurrent === 1) {
      if (!formValues.newTireSetId) {
        newErrors.newTireSetId = "Morate odabrati set guma za montažu na vozilo";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resolveNamesForSummary = async () => {
    const names: ResolvedDemountNames = {};
    try {
      // Resolve target skladište
      if (formValues.targetWarehouseId) {
        const w = await getFleetWarehouseAPI(formValues.targetWarehouseId);
        names.targetWarehouse = {
          name: w.name,
          address: [w.addressLine1, w.addressPostalCode, w.addressCity, w.addressCountry].filter(Boolean).join(", "),
        };
      }

      // Resolve novi set za montažu
      if (formValues.newTireSetId) {
        const s = await getFleetTireSetAPI(formValues.newTireSetId);
        const setForm = transformFleetTireSetEditForm(s);
        names.newTireSet = {
          code: s.code,
          dimension: getDimensionSet(setForm.tires),
          type: getTireType(setForm.tires)?.name ?? null,
          manufacturer: getTireManufacturers(setForm.tires) || null,
        };
      }
    } catch { /* */ }
    setResolvedNames(names);
  };

  const handleNextStep = async () => {
    if (!validateStep()) return;

    const nextStep = stepCurrent + 1;
    const summaryStepIndex = 2;

    // Resolve nazive prije ulaska u summary korak
    if (nextStep === summaryStepIndex) {
      await resolveNamesForSummary();
    }

    setStepCurrent(nextStep);
  };

  const handleBackStep = () => {
    setStepCurrent((s) => s - 1);
  };

  const handleFinish = async () => {
    setIsLoading(true);
    try {
      await demountFleetTireSetAPI(tireSetId, {
        targetWarehouseId: formValues.targetWarehouseId!,
        newTireSetId: formValues.newTireSetId,
        transferDate: formValues.transferDate,
        kmTraveled: formValues.kmTraveled!,
      }, t);

      setIsLoading(false);
      setShowSuccessOverlay(true);
    } catch {
      setIsLoading(false);
    }
  };

  const handleSuccessDismiss = () => {
    setShowSuccessOverlay(false);
    setFormValues(initialDemountFormValues);
    setStepCurrent(0);
    if (onSuccess) onSuccess();
  };

  const steps = useMemo(() => [
    <WizardStep key={1} stepNumber={0} stepCurrent={stepCurrent}>
      <DemountStep1
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        openUsage={openUsage}
        vehicleKm={currentSet.vehicle?.kmTraveled ?? null}
      />
    </WizardStep>,
    <WizardStep key={2} stepNumber={1} stepCurrent={stepCurrent}>
      <DemountStep2 formValues={formValues} tireSetId={tireSetId} onChange={handleChange} errors={errors} />
    </WizardStep>,
    <WizardStep key={3} stepNumber={2} stepCurrent={stepCurrent}>
      <DemountSummary
        currentSet={currentSet}
        formValues={formValues}
        resolvedNames={resolvedNames}
        openUsage={openUsage}
      />
    </WizardStep>,
  ], [stepCurrent, currentSet, formValues, errors, handleChange, resolvedNames, openUsage]);

  return (
    <>
      <SuccessOverlay
        show={showSuccessOverlay}
        animation={successAnimation}
        title="Demontaža je uspješno izvršena!"
        description="Set guma je skinut s vozila i spremljen na skladište."
        onDismiss={handleSuccessDismiss}
      />
      <Modal
        show={show}
        onHide={onClose ?? (() => {})}
        position="side"
        size="full"
        intro={{
          content: (
            <TireSetDemountIntro
              currentSet={currentSet}
              formValues={formValues}
              targetWarehouseName={resolvedNames.targetWarehouse?.name}
              newSetCode={resolvedNames.newTireSet?.code}
              newSetDimension={resolvedNames.newTireSet?.dimension}
              stepCurrent={stepCurrent}
              stepCount={steps.length}
            />
          ),
        }}
      >
        <ModalHeader
          icon="/media/images/add-icon.svg"
          title="Demontaža seta guma"
          onHide={onClose ?? (() => {})}
        />
        <ModalBody>
          {isLoading && (
            <div className="relative h-[calc(100vh-80px)]">
              <Loader isLoading={true}>{null}</Loader>
            </div>
          )}
          <div className={isLoading ? "hidden" : ""}>
            <Wizard steps={steps} />
          </div>
        </ModalBody>
        <WizardFooter
          stepCurrent={stepCurrent}
          steps={steps}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onFinish={handleFinish}
          isLoading={isLoading}
          isReadOnly={false}
        />
      </Modal>
    </>
  );
};

export default memo(TireSetDemountEditCard);
